import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/Remove';
import {
    Box,
    Button,
    Grid2 as Grid,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { Skeleton } from '@mui/material';
import { sum } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import IsMountedWrapper from '../../../utils/functions/isMountedWrapper';
import { BookingStore } from '../../../utils/stores/BookingStore';
import { ProductsStore } from '../../../utils/stores/ProductsStore';
import { gradients } from '../../../utils/theme';
import CalendarDateSel from './CalendarDateSel';
import SessionSelect from './SessionSelect';
import TourSelect from './TourSelect';

// Small all-caps field label, echoing the hero's "CAIRNS, AUSTRALIA" eyebrow.
const fieldLabelSx = {
    color: 'primary.main',
    letterSpacing: '0.14em',
    fontWeight: 700,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    mb: 1,
    width: '100%',
    textAlign: 'left',
} as const;

// Vertical breathing room between stacked fields on mobile + slight inset.
const fieldWrapperSx = {
    mb: { xs: 2.25, md: 0 },
    px: { xs: 0.5, sm: 0 },
} as const;

// Inline replacements for the old `@mui/styles` (makeStyles/JSS) classes. Legacy makeStyles
// is incompatible with MUI v6 + React 18: its ref-counted stylesheets can momentarily detach
// during the booking widget's frequent availability re-renders, leaving the grid columns
// unstyled (collapsed/overlapping). Emotion `sx` injects deterministically, so no FOUC.
// Breakpoint mapping (MUI defaults xs 0 / sm 600 / md 900):
//   only('xs') / down('sm') -> < 600 -> `xs` (reset at `sm`)
//   only('sm')              -> 600..899 -> `sm` (reset at `md`)
//   down('md')              -> < 900 -> `xs`+`sm` (reset at `md`)
const availabilitySx = {
    container: {
        width: '100%',
        justifyContent: 'center',
        flexDirection: 'row',
        px: { xs: '20px', sm: 0 },
    },
    paper: {
        zIndex: { xs: 5, sm: 8 },
        borderRadius: '10px',
        width: '80%',
        '@media (max-width:999px)': { width: '100%' },
        padding: { xs: 0, sm: '5px 10px 30px', md: '15px 25px 20px' },
    },
    innerContainer: {
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'center', sm: 'stretch' },
    },
    innerGridItem: {
        width: '100%',
        // only('sm'): center the field on tablet; reset to the default block flow at md+
        // (an `sx` value set at `sm` would otherwise also apply to md/lg).
        display: { sm: 'flex', md: 'block' },
        flexDirection: { sm: 'column', md: 'row' },
        alignItems: { sm: 'center', md: 'normal' },
    },
    sectionItem: {
        display: { xs: 'flex', sm: 'block' },
        flexDirection: { xs: 'column' },
        alignItems: { xs: 'center' },
        width: { sm: '90%', md: 'auto' },
    },
    menuButton: {
        width: { xs: '100%', md: '90%' },
        height: 54,
        borderStyle: 'solid',
        borderWidth: 1,
        display: 'flex',
        justifyContent: 'space-between',
        pl: '14.5px',
        pr: '14.5px',
    },
    menuItem: { width: 280, pr: '2px' },
    buttonContainer: {
        justifyContent: 'center',
        alignContent: 'flex-end',
        mt: { xs: '25px', sm: 0 },
    },
    button: {
        width: { xs: '100%', sm: '80%' },
        height: 54,
        backgroundImage: gradients.circular,
        my: { xs: '30px', sm: 0 },
    },
    packageNote: {
        width: '100%',
        display: 'flex',
        mt: { xs: '15px', sm: '20px' },
        px: { xs: 0, sm: '2.5%', md: 0 },
        justifyContent: { xs: 'center', sm: 'flex-start' },
    },
    packageNoteText: {
        color: 'text.soft',
        fontWeight: 400,
        fontSize: 17,
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1.3,
        pr: { xs: 0, sm: '5%' },
    },
} as const;

interface BookingOptionsProps {
    /** Disable selection until a tour has been chosen (home page, before a tour is picked). */
    readonly disabled?: boolean;
}

const BookingOptions = ({ disabled }: BookingOptionsProps) => {
    const isMounted = IsMountedWrapper();
    const productController = useContext(ProductsStore);
    const bookingController = useContext(BookingStore);
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const sumOfFares = sum(bookingController.booking.quantities?.map((x) => x.value));

    const triggerBorderColor = disabled
        ? 'rgba(0,0,0,0.12)'
        : anchorEl
          ? theme.palette.primary.main
          : sumOfFares > 0
            ? 'green'
            : 'rgba(0,0,0,0.23)';
    // Enabled + nothing selected: use the same #333 the other inputs (selects + date) render
    // their text in, so "View Options" matches. Green once fares are chosen; muted while disabled.
    const triggerColor = disabled ? 'rgba(0,0,0,0.38)' : sumOfFares > 0 ? 'green' : '#333';

    const openMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const closeMenu = (event) => {
        setAnchorEl(null);
    };

    const addFare = (index: number) => {
        const participants = (bookingController.booking.participants || []).concat({
            fields: productController.product.participantInfoFields.map((x) => x),
        });
        bookingController.setBooking(
            (prev) => ({
                ...prev,
                quantities: prev.quantities.map((q, i) => (i === index ? { ...q, value: q.value + 1 } : q)),
                participants: participants,
            }),
            isMounted
        );
    };

    const minusFare = (index: number) => {
        const participants =
            bookingController.booking.participants.length > 1
                ? bookingController.booking.participants.slice(0, bookingController.booking.participants.length - 1)
                : [];
        bookingController.setBooking(
            (prev) => ({
                ...prev,
                quantities: prev.quantities.map((q, i) => (i === index ? { ...q, value: q.value - 1 } : q)),
                participants: participants,
            }),
            isMounted
        );
    };

    const optionsArray = productController.product?.priceOptions;
    return (
        <Grid sx={availabilitySx.sectionItem}>
            <Button
                onClick={openMenu}
                disabled={disabled}
                sx={availabilitySx.menuButton}
                style={{
                    // Full border shorthand inline: MUI ButtonBase sets `border: 0`, so only
                    // an inline shorthand (not the menuButton class) actually paints the outline.
                    border: `1px solid ${triggerBorderColor}`,
                    color: triggerColor,
                    // Match the outlined selects/date field: rounded corners + white field,
                    // muted fill when disabled (no tour chosen yet), sentence-case label.
                    borderRadius: 4,
                    backgroundColor: disabled ? 'rgba(0,0,0,0.02)' : '#fff',
                    textTransform: 'none',
                    fontWeight: 400,
                }}
                endIcon={anchorEl ? <ExpandLessIcon /> : <ArrowDropDownIcon />}
                disableFocusRipple
            >
                {sumOfFares > 0 ? `Qty ${sumOfFares} Selected` : 'View Options'}
            </Button>
            <Menu
                id="fare-type-select"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={closeMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                {optionsArray &&
                    optionsArray?.[0]?.label === bookingController.booking?.quantities?.[0]?.label &&
                    optionsArray.map((option, i) => {
                        const index = bookingController.booking?.quantities?.findIndex((x) => x.label === option.label);
                        return (
                            <MenuItem key={`fare option ${i + 1}`} sx={availabilitySx.menuItem}>
                                {index !== -1 && option.label && option.label.length > 0 ? (
                                    <>
                                        <ListItemText
                                            primary={
                                                option?.label.length > 17
                                                    ? `${option.label.substring(0, 18)}...`
                                                    : option.label
                                            }
                                        />
                                        <IconButton
                                            aria-label="minus"
                                            disabled={bookingController.booking.quantities[index].value === 0}
                                            onClick={() => minusFare(index)}
                                        >
                                            <RemoveIcon />
                                        </IconButton>
                                        <Typography style={{ padding: '0 5px' }}>
                                            {bookingController.booking.quantities[index].value}
                                        </Typography>
                                        <IconButton
                                            aria-label="plus"
                                            disabled={
                                                bookingController.booking.quantities[index].value ===
                                                productController.product.maxQuantity
                                            }
                                            onClick={() => addFare(index)}
                                        >
                                            <AddIcon />
                                        </IconButton>
                                    </>
                                ) : (
                                    <Skeleton width={100} height={40} />
                                )}
                            </MenuItem>
                        );
                    })}
            </Menu>
        </Grid>
    );
};

export default function CheckAvailability(props) {
    const { showTourSelect } = props;
    const paperRef = useRef<HTMLHeadingElement>(null);
    const [bookingHeight, setBookingHeight] = useState(0);
    const overlayBooking = useMediaQuery('(min-width: 1000px');
    const theme = useTheme();
    const isXS = useMediaQuery(theme.breakpoints.only('xs'));
    const productController = useContext(ProductsStore);

    // One shared layout for both pages: columns grow to fill, with the date column given a
    // wider fixed share on desktop (full month name + calendar icon). The Tour column is the
    // only structural difference between the home and product-page variants.
    const colSize = { xs: 12, sm: 6, md: 'grow' } as const;
    const dateColSize = { xs: 12, sm: 6, md: 3 } as const;
    const productSelected = Boolean(productController.product?.productCode);

    const renderLabel = (text: string) => (
        <Typography component="p" sx={fieldLabelSx}>
            {text}
        </Typography>
    );
    const paperMarginTop = showTourSelect
        ? isXS
            ? 10
            : 0
        : overlayBooking
          ? `-${bookingHeight * 0.5}px`
          : isXS
            ? 10
            : 30;

    const calcHeight = () => {
        const height = paperRef.current?.offsetHeight;
        setBookingHeight(height);
    };

    useEffect(() => {
        calcHeight();
        window.addEventListener('resize', calcHeight);
        return () => {
            window.removeEventListener('resize', calcHeight);
        };
    }, []);

    useEffect(() => {
        calcHeight();
    }, [productController.product?.allowPackage]);

    return (
        <Grid container sx={availabilitySx.container}>
            <Paper
                elevation={showTourSelect ? 0 : isXS ? 0 : 2}
                sx={availabilitySx.paper}
                ref={paperRef}
                style={{
                    marginTop: paperMarginTop,
                    marginBottom: 30,
                    // Home variant: a clean white floating card so the fields pop against the dark band.
                    ...(showTourSelect
                        ? {
                              backgroundColor: '#fff',
                              borderRadius: 16,
                              padding: isXS ? '18px 16px' : '24px 28px',
                              boxShadow: '0 22px 50px rgba(0,0,0,0.28)',
                          }
                        : {}),
                }}
            >
                <Grid container sx={availabilitySx.innerContainer}>
                    {showTourSelect && (
                        <Grid size={colSize} sx={[fieldWrapperSx, availabilitySx.innerGridItem]}>
                            {renderLabel('Tour')}
                            <TourSelect />
                        </Grid>
                    )}
                    <Grid size={dateColSize} sx={[fieldWrapperSx, availabilitySx.innerGridItem]}>
                        {renderLabel('Date')}
                        <CalendarDateSel />
                    </Grid>
                    <Grid size={colSize} sx={[fieldWrapperSx, availabilitySx.innerGridItem]}>
                        {renderLabel('Time')}
                        <SessionSelect />
                    </Grid>
                    <Grid size={colSize} sx={[fieldWrapperSx, availabilitySx.innerGridItem]}>
                        {renderLabel('Fare Types')}
                        <BookingOptions disabled={showTourSelect && !productSelected} />
                    </Grid>
                    <Grid container sx={availabilitySx.buttonContainer} size={colSize}>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={availabilitySx.button}
                            onClick={props.handleClickOpen}
                        >
                            Book Now
                        </Button>
                    </Grid>
                </Grid>
                {productController.product?.allowPackage && (
                    <Box component="div" sx={availabilitySx.packageNote}>
                        <InfoIcon color="primary" fontSize="large" style={{ marginRight: 10 }} />
                        <Typography sx={availabilitySx.packageNoteText}>
                            Start by choosing a departure time for the{' '}
                            {productController.product.packageProductNames[0]} portion of your package tour.
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Grid>
    );
}
